import { useNavigate } from 'react-router'
import { toJS } from 'mobx'
import { type ActionItem, type IActionsProps } from 'shared/ui'
import { truthy } from 'shared/lib'
import { ChatbotInstructionType, type ChatbotListItem } from 'entities/Chatbot'
import { ChatbotPath } from 'entities/ExportRouters'
import { webhookAction } from 'widgets/EnrollByWebhook/actions/webhookAction/webhookAction.tsx'
import { useChatbotDetailsContext, useChatbotListContext } from 'pages/chatbot/context'
import { ChatbotRoutes } from 'pages/chatbot'
import { handleDuplicate } from 'pages/chatbot/actions/duplicate'

export type IChatbotRow = {
  id: number
  payload: ChatbotListItem
}

export type IChatbotActionRow = IChatbotRow & {
  actionsProps: IActionsProps
}

export const useRowsWithActions: () => IChatbotActionRow[] = () => {
  const { items, deleteRow } = useChatbotListContext()
  const { showDetails } = useChatbotDetailsContext()
  const navigate = useNavigate()

  const makeEditAction = (item: ChatbotListItem): ActionItem | null => {
    const path =
      item?.type === ChatbotInstructionType.BookAppointment
        ? `/${ChatbotPath.viewBookAppointment(item.id)}`
        : `/${ChatbotRoutes.root}/${item.id}`

    return {
      icon: 'edit',
      text: 'Edit',
      onAction: () => navigate(path, { state: toJS(item) }),
    }
  }

  const makeDetailsAction = (item: ChatbotListItem): ActionItem | null => {
    return {
      icon: 'eye',
      text: 'Details',
      onAction: () => showDetails(item.id),
    }
  }

  const makeDeleteAction = (item: ChatbotListItem): ActionItem | null => {
    return {
      icon: 'delete',
      text: 'Delete',
      onAction: () => deleteRow(item.id),
    }
  }

  const makeDuplicateAction = (item: ChatbotListItem): ActionItem | null => {
    return {
      icon: 'copy',
      text: 'Duplicate',
      onAction: () => {
        handleDuplicate(item.type, item.id)
      },
    }
  }

  return items.map((item) => {
    const actions = [
      makeEditAction(item),
      makeDetailsAction(item),
      item.origin.webhook_url &&
        webhookAction({
          webhookUrl: item.origin.webhook_url,
          source: 'AI Agent',
          sourceName: item.name,
        }),
      makeDuplicateAction(item),
      makeDeleteAction(item),
    ].filter(truthy)

    return { id: item.id, payload: item, actionsProps: { actions } }
  })
}
